import React from 'react';
import { motion } from 'framer-motion';
import { Star, Shield, Users, Sparkles, Target, Zap } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
interface AboutPageProps {
  onNavigate: (page: string) => void;
}
export function AboutPage({
  onNavigate
}: AboutPageProps) {
  return <div className="min-h-screen bg-white pb-24" data-id="element-16275">
      {/* Hero Section */}
      <div className="bg-slate-50 border-b border-slate-200 pt-6 pb-20" data-id="element-16276">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-16277">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'О нас'
        }]} className="mb-12" data-id="element-16278" />

          <div className="max-w-3xl mx-auto text-center" data-id="element-16279">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-16280">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-16281">
                Делаем профессиональную астрологию{' '}
                <span className="text-indigo-600" data-id="element-16282">доступной</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-10" data-id="element-16283">
                Astriqo — это платформа нового поколения, объединяющая точные
                алгоритмы расчета, глубокие знания профессиональных астрологов и
                современные технологии.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-16284">
                <Button size="lg" onClick={() => onNavigate('service-natal')} data-id="element-16285">
                  Рассчитать карту
                </Button>
                <Button variant="outline" size="lg" onClick={() => onNavigate('astrologers')} data-id="element-16286">
                  Наши астрологи
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-12" data-id="element-16287">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 sm:p-12 relative z-10" data-id="element-16288">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center" data-id="element-16289">
            <div className="sm:border-r sm:border-slate-100" data-id="element-16290">
              <div className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-2" data-id="element-16291">
                100k+
              </div>
              <div className="text-slate-500 font-medium" data-id="element-16292">
                Сгенерированных отчетов
              </div>
            </div>
            <div className="sm:border-r sm:border-slate-100" data-id="element-16293">
              <div className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-2" data-id="element-16294">
                50+
              </div>
              <div className="text-slate-500 font-medium" data-id="element-16295">
                Проверенных астрологов
              </div>
            </div>
            <div data-id="element-16296">
              <div className="text-4xl sm:text-5xl font-bold text-indigo-600 mb-2" data-id="element-16297">
                98%
              </div>
              <div className="text-slate-500 font-medium" data-id="element-16298">
                Довольных клиентов
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission & Values */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-16299">
        <div className="text-center mb-16" data-id="element-16300">
          <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-16301">
            Наши принципы
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-16302">
            Мы верим, что астрология — это мощный инструмент самопознания,
            который должен быть точным, понятным и полезным.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-16303">
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-shadow" data-id="element-16304">
            <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mb-6" data-id="element-16305">
              <Target className="w-6 h-6" data-id="element-16306" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-16307">
              Высокая точность
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-16308">
              Наши алгоритмы используют швейцарские эфемериды NASA (Swiss
              Ephemeris), обеспечивая астрономическую точность расчетов,
              сопоставимую с профессиональным ПО.
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-shadow" data-id="element-16309">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-6" data-id="element-16310">
              <Shield className="w-6 h-6" data-id="element-16311" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-16312">
              Строгий отбор
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-16313">
              Каждый астролог на платформе проходит многоэтапную проверку: мы
              проверяем дипломы, проводим собеседования и анализируем отзывы
              клиентов.
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-shadow" data-id="element-16314">
            <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center mb-6" data-id="element-16315">
              <Sparkles className="w-6 h-6" data-id="element-16316" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-16317">
              Понятный язык
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-16318">
              Мы переводим сложный язык звезд на понятный человеческий. Наши
              отчеты написаны профессиональными астрологами, но читаются легко и
              увлекательно.
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-16319">
        <div className="bg-slate-900 rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden" data-id="element-16320">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-16321" />
          <div className="relative z-10" data-id="element-16322">
            <h2 className="text-3xl font-bold mb-4" data-id="element-16323">
              Готовы познакомиться с собой?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto" data-id="element-16324">
              Постройте свою натальную карту бесплатно и узнайте, как планеты
              влияют на вашу жизнь, характер и судьбу.
            </p>
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100" onClick={() => onNavigate('free-chart')} data-id="element-16325">
              Начать бесплатно
            </Button>
          </div>
        </div>
      </div>
    </div>;
}